/**
 * hearo — Track Toggle Web Component (v2, radiogroup pattern)
 *
 * Swaps between "Self-Buyer" (Track A) and "Gift-Giver" (Track B) content
 * based on user preference, persisted in localStorage.
 *
 * Changes vs v1:
 *   - role="radiogroup"/role="radio" + aria-checked (WAI-ARIA Authoring
 *     Practices for two mutually-exclusive choices — correct semantic).
 *   - Roving tabindex: only the checked radio is in the tab order.
 *   - Arrow keys move selection AND focus (standard radiogroup UX).
 *   - Space/Enter on a focused (unchecked) radio selects it.
 *   - Removed the container-level click-hijacking from v1 — CTAs are the
 *     single navigation path (keyboard/screen-reader friendly, no misleading
 *     cursor on non-interactive surfaces).
 *
 * Applies a `data-hearo-track` attribute to <html> so global CSS can target
 * the state across all sections on the page, not just the hero. The
 * FOUC-free init lives inline in theme.liquid and runs before sections
 * render; this component syncs when the user changes their choice.
 */

class HearoTrackToggle extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'hearo-track';
  }

  connectedCallback() {
    let stored = null;
    try {
      stored = localStorage.getItem(this.storageKey);
    } catch (err) {
      /* localStorage blocked (private mode / storage disabled) — fall through to default */
    }

    const defaultTrack = this.dataset.default || 'self';
    const current = stored || defaultTrack;
    this.setActiveTrack(current);

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleClick(e) {
    const option = e.target.closest('[data-track-option]');
    if (!option) return;
    const track = option.dataset.trackOption;
    this.setActiveTrack(track);
    /* Re-focus the clicked radio so keyboard users who tab in resume there */
    option.focus();
  }

  handleKeydown(e) {
    const option = e.target.closest('[data-track-option]');
    if (!option) return;

    const key = e.key;

    /* Space / Enter on a radio selects it (unchecked → checked) */
    if (key === ' ' || key === 'Enter') {
      e.preventDefault();
      this.setActiveTrack(option.dataset.trackOption);
      option.focus();
      return;
    }

    /* Arrow keys switch between tracks and move focus (radiogroup pattern) */
    const forward = key === 'ArrowRight' || key === 'ArrowDown';
    const backward = key === 'ArrowLeft' || key === 'ArrowUp';
    if (!forward && !backward) return;

    e.preventDefault();
    const current = this.getAttribute('data-track') || 'self';
    const next = current === 'self' ? 'gift' : 'self';
    this.setActiveTrack(next);
    const nextBtn = this.querySelector(`[data-track-option="${next}"]`);
    if (nextBtn) nextBtn.focus();
  }

  setActiveTrack(track) {
    if (track !== 'self' && track !== 'gift') return;

    this.setAttribute('data-track', track);
    document.documentElement.setAttribute('data-hearo-track', track);

    try {
      localStorage.setItem(this.storageKey, track);
    } catch (err) {
      /* silent fail */
    }

    this.querySelectorAll('[data-track-option]').forEach((btn) => {
      const isActive = btn.dataset.trackOption === track;
      /* Radios (pill toggle) use aria-checked + roving tabindex.
         Plain buttons (e.g. the corner dots) use aria-pressed and stay
         in the tab order — they are a secondary shortcut, not the
         primary radiogroup. */
      if (btn.getAttribute('role') === 'radio') {
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      } else {
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    });

    /* Let other sections react (e.g. the gift section can highlight itself) */
    this.dispatchEvent(
      new CustomEvent('hearo:trackchange', {
        detail: { track },
        bubbles: true,
      }),
    );
  }
}

if (!customElements.get('hearo-track-toggle')) {
  customElements.define('hearo-track-toggle', HearoTrackToggle);
}
